import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Link,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VideocamIcon from '@mui/icons-material/Videocam';
import GroupIcon from '@mui/icons-material/Group';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DirectionsIcon from '@mui/icons-material/Directions';
import { usePublicEventPage } from '../hooks/useEventPage';

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatEventDate(dateStr: string, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: timezone,
    }).format(new Date(dateStr));
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(dateStr));
  }
}

function formatEventTime(dateStr: string, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
      timeZoneName: 'short',
    }).format(new Date(dateStr));
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(dateStr));
  }
}

function formatFullDateTime(dateStr: string, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
      timeZoneName: 'short',
    }).format(new Date(dateStr));
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(dateStr));
  }
}

function getVirtualPlatformLabel(type: string | null | undefined): string {
  switch (type) {
    case 'zoom':
      return 'Zoom';
    case 'google_meet':
      return 'Google Meet';
    case 'teams':
      return 'Microsoft Teams';
    default:
      return 'Online';
  }
}

/**
 * Simple HTML sanitizer that strips script tags, event handlers,
 * and dangerous attributes. No external dependency needed.
 */
function sanitizeHtml(html: string): string {
  // Remove script tags and their content
  let clean = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  // Remove on* event handler attributes
  clean = clean.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
  // Remove javascript: URLs
  clean = clean.replace(/href\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi, '');
  clean = clean.replace(/src\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi, '');
  // Remove style expressions (IE)
  clean = clean.replace(/style\s*=\s*(?:"[^"]*expression\s*\([^"]*"|'[^']*expression\s*\([^']*')/gi, '');
  // Remove iframe, object, embed, form tags
  clean = clean.replace(/<\/?(?:iframe|object|embed|form|input|textarea|button)\b[^>]*>/gi, '');
  return clean;
}

// Google Calendar URL builder
function buildGoogleCalendarUrl(
  title: string,
  dateStr: string,
  endDateStr: string | null | undefined,
  timezone: string,
  location: string | null | undefined,
  description: string | null | undefined,
): string {
  const start = new Date(dateStr);
  const end = endDateStr
    ? new Date(endDateStr)
    : new Date(start.getTime() + 2 * 60 * 60 * 1000); // default 2 hours

  const fmt = (d: Date) =>
    d
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}/, '');

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${fmt(start)}/${fmt(end)}`,
    ctz: timezone,
  });
  if (location) params.set('location', location);
  if (description) params.set('details', description.slice(0, 500));

  return `https://calendar.google.com/calendar/event?${params.toString()}`;
}

// ── Loading Skeleton ────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <Container maxWidth="md" sx={{ py: 0 }}>
      <Skeleton
        variant="rectangular"
        height={300}
        sx={{ borderRadius: 0, mb: 0 }}
      />
      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
        <Skeleton variant="text" width="60%" height={48} />
        <Skeleton variant="text" width="40%" height={28} sx={{ mt: 1 }} />
        <Skeleton
          variant="rectangular"
          height={200}
          sx={{ borderRadius: 2, mt: 3 }}
        />
      </Box>
    </Container>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────

export default function PublicEventPage() {
  const { slug } = useParams<{ slug: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data, isLoading, isError } = usePublicEventPage(slug);

  // Set noindex meta tag
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  // Update page title
  useEffect(() => {
    if (data?.event.title) {
      document.title = `${data.event.title} | TimeToRSVP`;
    }
    return () => {
      document.title = 'TimeToRSVP';
    };
  }, [data?.event.title]);

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (isError || !data) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Event Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              This event page doesn't exist or is no longer available.
            </Typography>
          </CardContent>
        </Card>
        <Footer />
      </Container>
    );
  }

  const { event, faqs, rsvpSummary, attendingGuests } = data;
  const hasBanner = !!event.bannerImageUrl;
  const bannerHeight = isMobile ? 300 : 400;
  const calendarUrl = buildGoogleCalendarUrl(
    event.title,
    event.dateTime,
    event.endDateTime,
    event.timezone,
    event.location,
    event.description,
  );

  const directionsUrl = event.location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`
    : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Banner ── */}
      {hasBanner ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: bannerHeight,
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src={event.bannerImageUrl!}
            alt={event.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          {/* Gradient overlay */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '60%',
              background:
                'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
            }}
          />
          {/* Title overlaid on banner */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: { xs: 3, sm: 5 },
            }}
          >
            <Container maxWidth="md" disableGutters>
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                fontWeight={700}
                color="white"
                sx={{
                  textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                }}
              >
                {event.title}
              </Typography>
            </Container>
          </Box>
        </Box>
      ) : (
        /* No banner — colored header */
        <Box
          sx={{
            background: 'linear-gradient(135deg, #5C6BC0 0%, #3949AB 100%)',
            py: { xs: 5, sm: 7 },
            px: { xs: 3, sm: 5 },
          }}
        >
          <Container maxWidth="md" disableGutters>
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              fontWeight={700}
              color="white"
            >
              {event.title}
            </Typography>
          </Container>
        </Box>
      )}

      {/* ── Content ── */}
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 4 } }}>
        {/* Date/Time Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack spacing={2.5}>
              {/* Date & Time */}
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <EventIcon color="primary" sx={{ mt: 0.3 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {formatEventDate(event.dateTime, event.timezone)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {formatEventTime(event.dateTime, event.timezone)}
                    {event.endDateTime &&
                      ` - ${formatEventTime(event.endDateTime, event.timezone)}`}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CalendarMonthIcon />}
                  href={calendarUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    display: { xs: 'none', sm: 'inline-flex' },
                  }}
                >
                  Add to Calendar
                </Button>
              </Stack>

              {/* Mobile calendar button */}
              <Button
                variant="outlined"
                fullWidth
                startIcon={<CalendarMonthIcon />}
                href={calendarUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              >
                Add to Calendar
              </Button>

              {/* Timezone */}
              <Stack direction="row" alignItems="center" spacing={2}>
                <AccessTimeIcon color="action" sx={{ fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  All times shown in {event.timezone}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Location Card */}
        {(event.location || event.mapEmbedUrl) && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack spacing={2}>
                {event.location && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <LocationOnIcon color="primary" />
                      <Box>
                        {event.locationUrl ? (
                          <Link
                            href={event.locationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            color="inherit"
                          >
                            <Typography variant="body1" fontWeight={500}>
                              {event.location}
                            </Typography>
                          </Link>
                        ) : (
                          <Typography variant="body1" fontWeight={500}>
                            {event.location}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    {directionsUrl && (
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<DirectionsIcon />}
                        href={directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          flexShrink: 0,
                          display: { xs: 'none', sm: 'inline-flex' },
                        }}
                      >
                        Directions
                      </Button>
                    )}
                  </Stack>
                )}
                {/* Mobile directions button */}
                {directionsUrl && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<DirectionsIcon />}
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                  >
                    Get Directions
                  </Button>
                )}
                {/* Embedded map */}
                {event.mapEmbedUrl && (
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      component="iframe"
                      src={event.mapEmbedUrl}
                      width="100%"
                      height={isMobile ? 200 : 300}
                      sx={{ border: 0, display: 'block' }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Event location map"
                      allowFullScreen
                    />
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Virtual Event Card */}
        {event.virtualEventUrl && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <VideocamIcon color="primary" />
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {getVirtualPlatformLabel(event.virtualEventType)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This event has a virtual component
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  href={event.virtualEventUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ flexShrink: 0 }}
                >
                  Join Online
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Description */}
        {(event.descriptionHtml || event.description) && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Typography
                variant="h6"
                fontWeight={600}
                gutterBottom
                sx={{ mb: 2 }}
              >
                About This Event
              </Typography>
              {event.descriptionHtml ? (
                <Box
                  sx={{
                    '& p': { mt: 0, mb: 1.5 },
                    '& a': { color: 'primary.main' },
                    '& img': { maxWidth: '100%', borderRadius: 1 },
                    '& ul, & ol': { pl: 3 },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      mt: 2,
                      mb: 1,
                    },
                    lineHeight: 1.7,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(event.descriptionHtml),
                  }}
                />
              ) : (
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                >
                  {event.description}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* FAQ Section */}
        {faqs.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Frequently Asked Questions
              </Typography>
              {faqs.map((faq) => (
                <Accordion
                  key={faq.id}
                  disableGutters
                  elevation={0}
                  sx={{
                    '&:before': { display: 'none' },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    mb: 1,
                    '&:last-of-type': { mb: 0 },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={500}>{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'pre-wrap' }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>
        )}

        {/* RSVP Summary (if showGuestList) */}
        {event.showGuestList && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ mb: 2 }}
              >
                <GroupIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Who's Coming
                </Typography>
              </Stack>

              {/* Counts */}
              <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                {rsvpSummary.yes > 0 && (
                  <Chip
                    label={`${rsvpSummary.yes} attending`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                )}
                {rsvpSummary.maybe > 0 && (
                  <Chip
                    label={`${rsvpSummary.maybe} maybe`}
                    color="warning"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Stack>

              {/* Attending names (first names only) */}
              {attendingGuests.length > 0 && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    useFlexGap
                    spacing={1}
                  >
                    {attendingGuests.map((name, idx) => (
                      <Chip
                        key={idx}
                        label={name}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: '16px' }}
                      />
                    ))}
                  </Stack>
                </>
              )}

              {rsvpSummary.yes === 0 && rsvpSummary.maybe === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No responses yet. Be the first!
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Event Status */}
        {event.status === 'COMPLETED' && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" gutterBottom>
                This event has ended
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatFullDateTime(event.dateTime, event.timezone)}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Footer />
      </Container>
    </Box>
  );
}

// ── Footer ──────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      <Typography variant="caption" color="text.secondary">
        Powered by{' '}
        <Link href="/" color="inherit" underline="hover" fontWeight={500}>
          TimeToRSVP
        </Link>
      </Typography>
    </Box>
  );
}
